// EDEN · Brief 5 Item Bank client.
//
// The generated index carries every card plus the accepted Brief 4 evidence. Selection fails
// closed: unknown/excluded evidence, a band mismatch, or an incomplete/wrong asset returns a typed
// rejection. Provenance is stored inside block content so the existing API preserves it unchanged.

import { newBlock, type EditorBlock } from "./blocks";

export type ItemBankBand = "starters" | "movers" | "flyers" | "key" | "pet";
export type ItemBankAssetType = "source-card" | "prompt" | "student-sheet" | "answer-key" | "teacher-script" | "visual" | "audio" | "unsupported";

export interface ItemBankAsset {
  type: ItemBankAssetType;
  path: string;
  sha256: string | null;
  bytes: number | null;
  exists: boolean;
  media_type: string | null;
  data_url: string | null;
}

export interface TaughtGradeEvidence {
  mode?: string;
  required_nodes?: string[];
  lesson_bounds?: Array<{ node: string; term?: number | null; week?: number | null; book?: string | null; unit?: unknown; lessons?: string | null }>;
  missing?: string[];
}

export interface ItemBankCard {
  id: string;
  band: ItemBankBand | null;
  source_band: string;
  grade_scope: number[];
  title: string;
  component: string;
  skill_task_family: string;
  task_family: string;
  section: string;
  sets: string[];
  /** ⚠ The HOUSE CURRICULUM level (L1 Learn · L2 Practise · L3 Apply · L4 Test), NOT the TOS
   *  thinking level. It limits what CONTENT a paper may draw on, never how an item asks.
   *  `curriculum_level` carries the same value under an unambiguous name; prefer that one.
   *  See the K/C/A note above `blockFromCard` before using either. */
  bloom: string;
  /** The house L1–L4 content level, under a name that cannot be mistaken for Bloom's K/C/A. */
  curriculum_level: string;
  /** The card's authored TOS thinking level — a DISTRIBUTION over the card's items, summing to
   *  `items` ("C1 A2" = 3 items, 1 Comprehension + 2 Application). Null when the card states
   *  none, which is NOT "all Knowledge". Measured 2026-09-18: 84 of 589 cards carry one and every
   *  one of those sums exactly to its item count; 0 of the 29 selectable cards carry one. */
  kca: { k: number; c: number; a: number } | null;
  difficulty: string;
  marks: number;
  items: number;
  time: string;
  taught: {
    status: "candidate" | "eligible" | "excluded" | "unknown";
    reasons: string[];
    by_grade: Record<string, TaughtGradeEvidence>;
  };
  card_version_sha256: string;
  source_path: string;
  source_reference: string | null;
  licence_tier: string | null;
  printable_mode: string | null;
  asset_requirements: string[];
  assets: ItemBankAsset[];
  selectable: boolean;
  rejection_reasons: string[];
  item: string;
  key: string;
  teacher_script: string;
}

export interface ItemBankIndex {
  /** 3 since JOB-W04 (2026-09-18): the index carries `kca` and `curriculum_level`. */
  schema: 3;
  source_manifest_sha256: string;
  cards: ItemBankCard[];
}

export interface ItemBankBlockProvenance {
  schema: 1;
  card_id: string;
  card_version_sha256: string;
  source_path: string;
  source_reference: string | null;
  band: ItemBankBand;
  grade_scope: number[];
  component: string;
  skill_task_family: string;
  difficulty: string;
  /** The card's authored K/C/A distribution, or null when it states none. Present only on blocks
   *  imported since JOB-W04; an older saved block simply omits the key, and stays self-consistent
   *  because `provenance_snapshot` is recomputed from the stored fields, not from today's shape. */
  kca?: { k: number; c: number; a: number } | null;
  marks: number;
  items: number;
  taught: ItemBankCard["taught"];
  licence_tier: string;
  printable_mode: string;
  assets: ItemBankAsset[];
  block_type: EditorBlock["type"];
  /** Canonical imported block content before provenance was attached. The editor treats this
   * content as immutable; the check also makes direct/stale API edits lose accepted provenance. */
  content_snapshot: string;
  /** Canonical provenance fields, excluding this field. This makes metadata mutation visible. */
  provenance_snapshot: string;
}

export interface ItemBankRejection {
  ok: false;
  kind: "item-bank-rejection";
  card_id: string;
  reasons: string[];
}

export interface ItemBankAllocationReason {
  code: string;
  card_id?: string;
  assessment_id?: string;
  academic_year?: string;
  assessment_slot?: string;
  node_id?: string;
}

export interface ItemBankAllocationRejection {
  card_id: string;
  card_version_sha256: string;
  reasons: ItemBankAllocationReason[];
}

export interface ProgrammeAllocationSlot {
  grade: number;
  slot: "M1" | "M2" | "SA1";
  taughtThroughNode: string;
  academicYear: string;
}

export interface ProgrammeAllocationResult {
  allocations: Array<ProgrammeAllocationSlot & { card_id: string; card_version_sha256: string }>;
  shortages: Array<ProgrammeAllocationSlot & { reasons: string[] }>;
}

export interface ProgrammeHistoryUse {
  card_version_sha256: string;
  academicYear: string;
  slot: "M1" | "M2" | "SA1";
  confirmed: boolean;
}

export interface ProgrammeAllocationEvidence {
  /** Explicit authoritative snapshot; an empty array means history was checked and none exists. */
  history: readonly ProgrammeHistoryUse[];
  /** Must contain every full slot-context key, even when the server returned no rejections. */
  rejectionsBySlot: ReadonlyMap<string, readonly ItemBankAllocationRejection[]>;
}

export function programmeSlotEvidenceKey(slot: ProgrammeAllocationSlot): string {
  return `${slot.grade}:${slot.academicYear}:${slot.slot}:${slot.taughtThroughNode}`;
}

export interface ItemBankBlockSuccess {
  ok: true;
  block: EditorBlock;
}

export type BlockFromCardResult = ItemBankBlockSuccess | ItemBankRejection;

/** Merge server-authoritative history/allocation reasons into the existing Brief 5 decision. */
export function allocationReasonsForCard(
  card: ItemBankCard,
  rejections: readonly ItemBankAllocationRejection[],
): ItemBankAllocationReason[] {
  const entry = rejections.find((value) =>
    value.card_id === card.id && value.card_version_sha256 === card.card_version_sha256,
  );
  return entry?.reasons ?? [];
}

/** Stable one-card-per-slot allocation used by the Brief 6 capacity report. It never reuses an
 * exact version. If a slot cannot be filled, the result keeps the exact server/base reasons. */
export function allocateProgrammeSlots(
  cards: readonly ItemBankCard[],
  slots: readonly ProgrammeAllocationSlot[],
  evidence?: ProgrammeAllocationEvidence,
): ProgrammeAllocationResult {
  const usedVersions = new Set<string>();
  const allocations: ProgrammeAllocationResult["allocations"] = [];
  const shortages: ProgrammeAllocationResult["shortages"] = [];
  const orderedCards = [...cards].sort((left, right) => left.id.localeCompare(right.id));
  for (const slot of slots) {
    const key = programmeSlotEvidenceKey(slot);
    if (!evidence || !evidence.rejectionsBySlot.has(key)) {
      shortages.push({ ...slot, reasons: ["allocation_evidence_missing"] });
      continue;
    }
    const serverRejections = evidence.rejectionsBySlot.get(key)!;
    const contextReasons = programmeContextReasons(slot);
    const gradeCards = orderedCards.filter((card) => card.grade_scope.includes(slot.grade));
    const selected = gradeCards.find((card) =>
      card.selectable
      && !usedVersions.has(card.card_version_sha256)
      && contextReasons.length === 0
      && programmeTaughtReasons(card, slot).length === 0
      && programmeHistoryReasons(card, slot, evidence.history).length === 0
      && allocationReasonsForCard(card, serverRejections).length === 0,
    );
    if (selected) {
      usedVersions.add(selected.card_version_sha256);
      allocations.push({ ...slot, card_id: selected.id, card_version_sha256: selected.card_version_sha256 });
      continue;
    }
    const reasons = new Set<string>(contextReasons);
    if (!gradeCards.length) reasons.add("no_card_for_grade");
    for (const card of gradeCards) {
      if (!card.selectable) card.rejection_reasons.forEach((reason) => reasons.add(reason));
      if (usedVersions.has(card.card_version_sha256)) reasons.add("cross_slot_collision");
      programmeTaughtReasons(card, slot).forEach((reason) => reasons.add(reason));
      programmeHistoryReasons(card, slot, evidence.history).forEach((reason) => reasons.add(reason));
      allocationReasonsForCard(card, serverRejections).forEach((reason) => reasons.add(reason.code));
    }
    if (!reasons.size) reasons.add("no_eligible_unused_version");
    shortages.push({ ...slot, reasons: [...reasons].sort() });
  }
  return { allocations, shortages };
}

const PROGRAMME_TERM_WEEK_MAX: Record<number, readonly [number, number, number]> = {
  1: [15, 16, 14], 2: [13, 13, 12], 3: [13, 13, 11], 4: [12, 11, 11],
  5: [14, 15, 14], 6: [14, 15, 14], 7: [14, 15, 14], 8: [14, 15, 14],
  9: [14, 15, 14], 10: [14, 15, 14],
};

function programmeYearIndex(value: string): number | null {
  const match = /^(20\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const start = Number(match[1]);
  return start >= 2000 && start <= 2098 && Number(match[2]) === (start + 1) % 100 ? start : null;
}

function programmeNodeOrdinal(value: string, grade: number): number | null {
  const match = /^G(10|[1-9])-T([1-3])-(\d{2})$/.exec(value);
  if (!match || Number(match[1]) !== grade) return null;
  const term = Number(match[2]);
  const week = Number(match[3]);
  const max = PROGRAMME_TERM_WEEK_MAX[grade]?.[term - 1];
  return max && week >= 1 && week <= max ? term * 100 + week : null;
}

function programmeContextReasons(slot: ProgrammeAllocationSlot): string[] {
  const reasons = [];
  if (!Number.isInteger(slot.grade) || slot.grade < 1 || slot.grade > 10) reasons.push("invalid_grade");
  if (!programmeYearIndex(slot.academicYear)) reasons.push("invalid_academic_year");
  if (!["M1", "M2", "SA1"].includes(slot.slot)) reasons.push("invalid_assessment_slot");
  if (programmeNodeOrdinal(slot.taughtThroughNode, slot.grade) === null) reasons.push("invalid_taught_through_node");
  return reasons;
}

function programmeTaughtReasons(card: ItemBankCard, slot: ProgrammeAllocationSlot): string[] {
  const evidence = card.taught.by_grade[String(slot.grade)];
  if (!evidence || evidence.missing?.length) return ["taught_evidence_missing"];
  const required = new Set([
    ...(evidence.required_nodes ?? []),
    ...(evidence.lesson_bounds ?? []).map((bound) => bound.node).filter(Boolean),
  ]);
  if (!required.size) return ["taught_evidence_missing"];
  const through = programmeNodeOrdinal(slot.taughtThroughNode, slot.grade);
  if (through === null) return ["invalid_taught_through_node"];
  for (const node of required) {
    const ordinal = programmeNodeOrdinal(node, slot.grade);
    if (ordinal === null) return ["taught_evidence_missing"];
    if (ordinal > through) return ["taught_after_node"];
  }
  return [];
}

function programmeHistoryReasons(card: ItemBankCard, slot: ProgrammeAllocationSlot, history: readonly ProgrammeHistoryUse[]): string[] {
  const year = programmeYearIndex(slot.academicYear);
  if (year === null) return ["invalid_academic_year"];
  const reasons = new Set<string>();
  for (const use of history) {
    if (use.card_version_sha256 !== card.card_version_sha256) continue;
    const usedYear = programmeYearIndex(use.academicYear);
    if (usedYear === null) { reasons.add("history_evidence_invalid"); continue; }
    if (usedYear === year) reasons.add("cross_slot_collision");
    else if (use.confirmed && year - usedYear === 1) reasons.add("history_blocked_n1");
    else if (use.confirmed && year - usedYear === 2) reasons.add("history_blocked_n2");
    else if (use.confirmed && (year - usedYear === -1 || year - usedYear === -2)) reasons.add("future_history_conflict");
  }
  return [...reasons];
}

export class ItemBankCardRejectedError extends Error {
  readonly rejection: ItemBankRejection;
  constructor(rejection: ItemBankRejection) {
    super(`Item Bank card ${rejection.card_id} is unavailable: ${rejection.reasons.join(", ")}`);
    this.name = "ItemBankCardRejectedError";
    this.rejection = rejection;
  }
}

/**
 * An index at an older schema is REFUSED, not read leniently. A schema-2 index carries no `kca` at
 * all, and reading it anyway would let the app report "no card states a thinking level" — a
 * comfortable silence indistinguishable from the truth (CLAUDE.md §4.2). Fail closed instead.
 *
 * Split out of `loadItemBank` so it can be tested: as an inline arrow inside a dynamic import
 * chain it was unreachable from any test, and JOB-W04's mutation check (M19) proved it — deleting
 * the schema comparison outright made nothing go red.
 */
export function isSupportedIndex(index: { schema?: unknown; cards?: unknown } | null | undefined): boolean {
  return !!index && index.schema === 3 && Array.isArray(index.cards);
}

let cache: Promise<ItemBankIndex | null> | null = null;

export function loadItemBank(): Promise<ItemBankIndex | null> {
  if (!cache) {
    cache = import("../curriculum/itembank-index.json")
      .then((module) => (module.default ?? module) as unknown as ItemBankIndex)
      .then((index) => (isSupportedIndex(index) ? index : null))
      .catch(() => null);
  }
  return cache;
}

/** Exact owner-approved grade ladder. Grade 6 remains Flyers. */
export function bandForGrade(grade: number | null): ItemBankBand | null {
  if (grade === null || !Number.isInteger(grade)) return null;
  if (grade >= 1 && grade <= 3) return "starters";
  if (grade >= 4 && grade <= 5) return "movers";
  if (grade >= 6 && grade <= 7) return "flyers";
  if (grade >= 8 && grade <= 9) return "key";
  if (grade === 10) return "pet";
  return null;
}

export function filterCards(
  cards: ItemBankCard[],
  opts: { band?: string; grade?: number | null; query?: string; selectableOnly?: boolean } = {},
): ItemBankCard[] {
  const query = (opts.query ?? "").trim().toLowerCase();
  return cards.filter((card) => {
    if (opts.band && card.band !== opts.band) return false;
    if (opts.grade != null && !card.grade_scope.includes(opts.grade)) return false;
    if (opts.selectableOnly && !card.selectable) return false;
    if (!query) return true;
    return card.id.toLowerCase().includes(query) || card.title.toLowerCase().includes(query) ||
      card.component.toLowerCase().includes(query) || card.skill_task_family.toLowerCase().includes(query) ||
      card.sets.some((set) => set.toLowerCase().includes(query));
  });
}

const HASH_RE = /^[0-9a-f]{64}$/;
const ELIGIBLE_STATUSES = new Set(["candidate", "eligible"]);
const PRINTABLE_MODES = new Set(["student-sheet", "embedded-card"]);

function decodedAsset(asset: ItemBankAsset): Uint8Array | null {
  if (!asset.data_url) return null;
  const match = asset.data_url.match(/^data:([^;,]+);base64,([A-Za-z0-9+/]*={0,2})$/);
  if (!match || match[1] !== asset.media_type) return null;
  try {
    const binary = atob(match[2]!);
    return Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  } catch { return null; }
}

// Synchronous SHA-256 keeps rejectionForCard usable by the existing synchronous editor contract.
// Asset payloads are capped by the generator (1 MB images, 5 MB audio), so this bounded verifier
// does not introduce an unbounded main-thread operation.
const SHA256_K = new Uint32Array([
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
  0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
  0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
  0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
  0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
]);
const rotr = (value: number, bits: number) => (value >>> bits) | (value << (32 - bits));
function sha256Bytes(raw: Uint8Array): string {
  const paddedLength = Math.ceil((raw.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(raw); padded[raw.length] = 0x80;
  const view = new DataView(padded.buffer);
  const bitLength = raw.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);
  const h = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]);
  const w = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15]!, b = w[i - 2]!;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a,b,c,d,e,f,g,hh] = h;
    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e!, 6) ^ rotr(e!, 11) ^ rotr(e!, 25);
      const ch = (e! & f!) ^ (~e! & g!);
      const t1 = (hh! + s1 + ch + SHA256_K[i]! + w[i]!) >>> 0;
      const s0 = rotr(a!, 2) ^ rotr(a!, 13) ^ rotr(a!, 22);
      const maj = (a! & b!) ^ (a! & c!) ^ (b! & c!);
      const t2 = (s0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d! + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    for (const [i, value] of [a,b,c,d,e,f,g,hh].entries()) h[i] = (h[i]! + value!) >>> 0;
  }
  return [...h].map((value) => value.toString(16).padStart(8, "0")).join("");
}

function assetPayloadValid(asset: ItemBankAsset): boolean {
  if (asset.type !== "visual" && asset.type !== "audio") return true;
  const raw = decodedAsset(asset);
  if (!raw || raw.length !== asset.bytes || sha256Bytes(raw) !== asset.sha256) return false;
  const ascii = (start: number, end: number) => String.fromCharCode(...raw.slice(start, end));
  if (asset.media_type === "image/png") return raw.length >= 8 && [137, 80, 78, 71, 13, 10, 26, 10].every((b, i) => raw[i] === b);
  if (asset.media_type === "image/jpeg") return raw.length >= 3 && raw[0] === 0xff && raw[1] === 0xd8 && raw[2] === 0xff;
  if (asset.media_type === "image/webp") return raw.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP";
  if (asset.media_type === "image/svg+xml") return /<svg\b/i.test(new TextDecoder().decode(raw.slice(0, 512)));
  if (asset.media_type === "audio/mpeg") return raw.length >= 3 && (ascii(0, 3) === "ID3" || (raw[0] === 0xff && (raw[1]! & 0xe0) === 0xe0));
  if (asset.media_type === "audio/wav") return raw.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 12) === "WAVE";
  if (asset.media_type === "audio/ogg") return raw.length >= 4 && ascii(0, 4) === "OggS";
  return false;
}

function mcqParts(card: ItemBankCard): { options: string[]; correctIndex: number } | null {
  const matches = [...card.item.matchAll(new RegExp(OPTION_SET_RE.source, "g"))];
  if (matches.length !== 1) return null;
  const options = matches[0]!.slice(1, 4).map((value) => value.trim());
  const answer = card.key.trim().match(/^(?:\d+[.)]\s*)?([A-D])(?:[.):\s-]|$)/i)?.[1]?.toUpperCase();
  const correctIndex = answer ? answer.charCodeAt(0) - 65 : -1;
  return options.every(Boolean) && correctIndex >= 0 && correctIndex < options.length ? { options, correctIndex } : null;
}

export function rejectionForCard(card: ItemBankCard, grade: number | null = null): ItemBankRejection | null {
  const reasons = new Set(card.rejection_reasons ?? []);
  if (!card.selectable) reasons.add("card_not_selectable");
  if (!card.band) reasons.add("unsupported_grade_band");
  if (!ELIGIBLE_STATUSES.has(card.taught?.status)) reasons.add(`eligibility_${card.taught?.status || "unknown"}`);
  if (!PRINTABLE_MODES.has(card.printable_mode ?? "")) reasons.add("unsupported_printable_mode");
  if (!HASH_RE.test(card.card_version_sha256)) reasons.add("invalid_card_version_sha256");
  if (!card.licence_tier) reasons.add("missing_owner_set_licence_tier");
  if (!card.item.trim()) reasons.add("missing_prompt");
  if (!card.key.trim()) reasons.add("missing_answer_key");
  if (!Number.isInteger(card.items) || card.items <= 0) reasons.add("missing_items");
  if (!Number.isFinite(card.marks) || card.marks <= 0) reasons.add("missing_marks");
  if (grade !== null) {
    if (!card.grade_scope.includes(grade)) reasons.add("grade_scope_mismatch");
    if (bandForGrade(grade) !== card.band) reasons.add("grade_band_mismatch");
  }

  const validAsset = (asset: ItemBankAsset): boolean => asset.exists && HASH_RE.test(asset.sha256 ?? "") && Number.isInteger(asset.bytes) && (asset.bytes ?? 0) > 0 && assetPayloadValid(asset);
  for (const asset of card.assets) {
    if (!validAsset(asset)) reasons.add(`invalid_${asset.type}_asset`);
    const decoded = decodedAsset(asset);
    if ((asset.type === "visual" || asset.type === "audio") && decoded && sha256Bytes(decoded) !== asset.sha256) reasons.add("asset_payload_hash_mismatch");
    if (asset.type === "visual" && (!asset.media_type?.startsWith("image/") || !assetPayloadValid(asset))) reasons.add("visual_asset_wrong_type");
    if (asset.type === "audio" && (!asset.media_type?.startsWith("audio/") || !assetPayloadValid(asset))) reasons.add("audio_asset_wrong_type");
  }
  const has = (type: ItemBankAssetType) => card.assets.some((asset) => asset.type === type && validAsset(asset));
  if (!has("source-card")) reasons.add("missing_source_card");
  if (!has("prompt")) reasons.add("missing_prompt_asset");
  if (!has("student-sheet")) reasons.add("missing_student_sheet");
  if (!has("answer-key")) reasons.add("missing_answer_key");
  if (card.asset_requirements.includes("audio_or_teacher_script") && !has("audio") && !(has("teacher-script") && card.teacher_script.trim())) reasons.add("missing_audio_or_teacher_script");
  const visuals = card.assets.filter((asset) => asset.type === "visual" && validAsset(asset) && !!asset.data_url).length;
  if (card.asset_requirements.includes("visual") && visuals < (card.id === "IB-STA-G1-VR-001" ? 3 : 1)) reasons.add(card.id === "IB-STA-G1-VR-001" ? "missing_three_visual_assets" : "missing_visual_asset");
  if (inferType(card) === "mcq") {
    const optionSetCount = [...card.item.matchAll(new RegExp(OPTION_SET_RE.source, "g"))].length;
    if (optionSetCount !== 1) reasons.add(optionSetCount > 1 ? "unsupported_multiple_mcq_sets" : "missing_mcq_options");
    if (!mcqParts(card)) reasons.add("invalid_mcq_answer_key");
  }

  return reasons.size ? { ok: false, kind: "item-bank-rejection", card_id: card.id, reasons: [...reasons].sort() } : null;
}

const OPTION_SET_RE = /\[\s*A[.):-]?\s*(.+?)\s*[·–—-]\s*B[.):-]?\s*(.+?)\s*[·–—-]\s*C[.):-]?\s*(.+?)\s*\]/;

function inferType(card: ItemBankCard): EditorBlock["type"] {
  const haystack = `${card.component} ${card.section} ${card.skill_task_family}`.toLowerCase();
  if (haystack.includes("dictation") || haystack.includes("listening")) return "listening";
  if (OPTION_SET_RE.test(card.item)) return "mcq";
  // The generic gap block numbers every blank. Preserve source question grouping whenever one
  // source item contains multiple blanks by keeping the authored numbering as short-answer text.
  if ((card.item.match(/_{3,}/g) ?? []).length === card.items) return "gap_fill";
  return "short_answer";
}

const DIFF_CODE: Record<string, "E" | "M" | "D"> = { E: "E", M: "M", D: "D" };

/**
 * ⚠ THE DECISION JOB-W04 WAS ASKED TO MAKE, AND WHY IT IS NOT A `BLOOM_CODE` MAP.
 *
 * `F-2026-09-18-05` is real: an imported block arrives with `bloom: null`, and the TOS adapter used
 * to read that null as Knowledge, so a paper of accepted cards reported 100% K. The obvious repair —
 * a `BLOOM_CODE` map beside `DIFF_CODE`, turning the card's `bloom: L1/L2/L3` into k/c/a — was
 * measured and REJECTED. It would be worse than the defect it fixes.
 *
 *  1. The two fields are different axes, and the department says so in terms.
 *     `EDEN_Department_Test_Standard_v2_2026-07-30.md` §5.3 rule 4: "L1–L4 and K/C/A are different
 *     axes and do not map onto each other." L1–L4 is the HOUSE CONTENT scale on a week's node
 *     (L1 Learn · L2 Practise · L3 Apply · L4 Test) and limits what a paper may draw on; K/C/A is
 *     the TOS scale on an item and limits how it asks.
 *  2. The corpus agrees. Of the 84 source cards stating both `bloom:` and `kca:`, **16** contradict
 *     any L→K/C/A mapping outright — the card's own `kca` gives ZERO items to the letter the
 *     mapping would assign: 9 × `bloom: L1` with `kca: C1 A2` (no K), 4 × `bloom: L2` with
 *     `kca: K3` (no C), 3 × `bloom: L3` with `kca: K3` (no A). 9 + 4 + 3 = 16, which is 19.05% of
 *     84 — an earlier draft of this comment said "19", a PERCENTAGE transcribed as a count. The
 *     independent review caught it. Measured 2026-09-18 over `Assessments/Item Bank/**`.
 *  3. A precise falsehood is worse than an obvious one. "100% Knowledge" is visibly absurd and a
 *     teacher distrusts it. "K 47% · C 31% · A 22%", derived from the wrong axis, is plausible,
 *     and would be signed off.
 *
 * The card's REAL thinking level is `kca` — and it is a distribution over the card's items, not one
 * level, so it could not sit in a block's single `bloom` field even where it exists. It is applied
 * in `blocksForItemCount`'s derived rows instead, which is where the card's items already become
 * separate rows. Where a card states no `kca`, the block stays UNTAGGED and `assessment-tos.ts`
 * says so out loud — it never guesses.
 *
 * Measured scale, 2026-09-18: 84 of 589 cards carry a `kca`, and **0 of the 29 selectable cards do**.
 * So today a paper of accepted cards has no authored thinking level at all, and the honest panel
 * says exactly that. Filed for the owner beside P4-4 — it is the same schema question one axis over.
 */
export function kcaForCard(card: Pick<ItemBankCard, "kca">): { k: number; c: number; a: number } | null {
  const kca = card.kca;
  return kca && kca.k + kca.c + kca.a > 0 ? kca : null;
}

function attachProvenance(block: EditorBlock, card: ItemBankCard): EditorBlock {
  const contentSnapshot = JSON.stringify(block.content);
  const provenanceFields = {
    schema: 1 as const,
    card_id: card.id,
    card_version_sha256: card.card_version_sha256,
    source_path: card.source_path,
    source_reference: card.source_reference,
    band: card.band!,
    grade_scope: [...card.grade_scope],
    component: card.component,
    skill_task_family: card.skill_task_family,
    difficulty: card.difficulty,
    kca: card.kca,
    marks: card.marks,
    items: card.items,
    taught: structuredClone(card.taught),
    licence_tier: card.licence_tier!,
    printable_mode: card.printable_mode!,
    assets: structuredClone(card.assets),
    block_type: block.type,
    content_snapshot: contentSnapshot,
  };
  const provenance: ItemBankBlockProvenance = {
    ...provenanceFields,
    provenance_snapshot: JSON.stringify(provenanceFields),
  };
  block.content = { ...block.content, __itemBank: provenance };
  return block;
}

function buildRenderableBlock(card: ItemBankCard): EditorBlock {
  const type = inferType(card);
  const block = newBlock(type);
  block.marks = card.marks;
  block.difficulty = DIFF_CODE[card.difficulty] ?? null;

  if (type === "mcq") {
    const parts = mcqParts(card)!;
    block.content = { stem: card.item.replace(OPTION_SET_RE, "").trim(), options: parts.options, correctIndex: parts.correctIndex };
  } else if (type === "gap_fill") {
    block.content = {
      text: card.item.replace(/^>\s?/gm, ""),
      gaps: card.key.split(/[·\n]/).map((value) => value.trim().replace(/^\d+[.)]\s*/, "")).filter(Boolean),
    };
  } else if (type === "listening") {
    const itemLines = card.item.split("\n").map((line) => line.trim());
    const firstNumbered = itemLines.findIndex((line) => /^\d+[.)]/.test(line));
    const intro = firstNumbered > 0 ? itemLines.slice(0, firstNumbered).filter(Boolean).join(" ") : "";
    const items = itemLines.filter((line) => /^\d+[.)]/.test(line)).map((line) => line.replace(/^\d+[.)]\s*/, ""));
    const keyLines = card.key.split("\n").map((line) => line.trim()).filter((line) => /^\d+[.)]/.test(line)).map((line) => line.replace(/^\d+[.)]\s*/, ""));
    const key = keyLines.length === items.length && items.length > 0 ? keyLines : [card.key];
    block.content = {
      trackLabel: card.teacher_script || intro || card.section || "Read aloud",
      items: items.length ? items : [card.item],
      key,
    };
  } else {
    block.content = { question: card.item.replace(/^>\s?/gm, ""), lines: Math.max(1, card.items), key: card.key };
  }
  return attachProvenance(block, card);
}

export function blockFromCard(card: ItemBankCard): EditorBlock;
export function blockFromCard(card: ItemBankCard, options: { grade?: number | null; rejection: "return" }): BlockFromCardResult;
export function blockFromCard(card: ItemBankCard, options?: { grade?: number | null; rejection: "return" }): EditorBlock | BlockFromCardResult {
  const rejection = rejectionForCard(card, options?.grade ?? null);
  if (rejection) {
    if (options?.rejection === "return") return rejection;
    throw new ItemBankCardRejectedError(rejection);
  }
  const block = buildRenderableBlock(card);
  return options?.rejection === "return" ? { ok: true, block } : block;
}

export function itemBankProvenance(content: Record<string, unknown>): ItemBankBlockProvenance | null {
  const value = content.__itemBank;
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const provenance = value as Partial<ItemBankBlockProvenance>;
  if (provenance.schema !== 1 || typeof provenance.card_id !== "string" || !HASH_RE.test(provenance.card_version_sha256 ?? "") || typeof provenance.content_snapshot !== "string" || typeof provenance.provenance_snapshot !== "string") return null;
  const provenanceFields = { ...provenance } as Partial<ItemBankBlockProvenance>;
  delete provenanceFields.provenance_snapshot;
  if (JSON.stringify(provenanceFields) !== provenance.provenance_snapshot) return null;
  const current = { ...content };
  delete current.__itemBank;
  return JSON.stringify(current) === provenance.content_snapshot ? provenance as ItemBankBlockProvenance : null;
}

export function itemBankProvenanceForBlock(block: EditorBlock): ItemBankBlockProvenance | null {
  const provenance = itemBankProvenance(block.content);
  if (!provenance || block.type !== provenance.block_type || block.marks !== provenance.marks) return null;
  const expectedDifficulty = DIFF_CODE[provenance.difficulty] ?? null;
  return block.difficulty === expectedDifficulty ? provenance : null;
}

/** Feed the existing TOS engine one logical row per source item without changing the saved block.
 * Marks are divided only in this derived view, so totals stay exact and save/reload bytes do not. */
export function blocksForItemCount(blocks: EditorBlock[]): EditorBlock[] {
  return blocks.flatMap((block) => {
    const provenance = itemBankProvenanceForBlock(block);
    if (!provenance || provenance.items <= 1) return [block];
    // The card's authored K/C/A distribution is spread across the derived rows. Which row gets
    // which letter is ARBITRARY — the card records a distribution, not a per-item assignment — and
    // it is arbitrary-but-harmless only because the one rule that reads an item's bloom
    // (tos.ts rule 6) aggregates by mark, and these rows carry equal marks. ⚠ A future rule that
    // reads a SINGLE item's bloom would make this ordering load-bearing, and would be wrong.
    // A block the teacher has tagged themselves WINS: their judgement is authored, this is copied.
    const spread = block.bloom ? null : kcaSpread(provenance.kca ?? null, provenance.items);
    return Array.from({ length: provenance.items }, (_, index) => ({
      ...block,
      id: `${block.id}:source-item-${index + 1}`,
      marks: block.marks / provenance.items,
      bloom: spread ? spread[index]! : block.bloom,
    }));
  });
}

/** One bloom letter per derived row, from the card's distribution. Null when the card states no
 *  `kca`, or states one that does not account for exactly `items` items — a distribution that does
 *  not add up is not a distribution, and guessing the remainder is how a wrong split gets signed
 *  off. Measured 2026-09-18: all 84 cards carrying a `kca` sum exactly to their item count. */
function kcaSpread(kca: { k: number; c: number; a: number } | null, items: number): EditorBlock["bloom"][] | null {
  if (!kca) return null;
  const out: EditorBlock["bloom"][] = [];
  for (const level of ["k", "c", "a"] as const) for (let i = 0; i < kca[level]; i++) out.push(level);
  // One length check, not two. An earlier draft also tested `kca.k + kca.c + kca.a !== items`
  // up front; JOB-W04's mutation check (M16) removed that line and NO test went red, because
  // `out.length` is that sum by construction — the guard could never fire on its own. A redundant
  // guard reads like a second opinion and is not one, so it is gone rather than papered over with
  // a test written to keep it alive.
  return out.length === items ? out : null;
}

/** A note about a block whose derived item view could not be built as the card intended.
 *  `blocksForItemCount` cannot report this itself — it returns blocks — so the TOS panel asks for
 *  these separately and shows them. Before JOB-W04 the collapse below was entirely silent. */
export interface ItemCountNote {
  code: "item-count-collapsed" | "kca-unusable";
  blockId: string;
  cardId: string;
  message: string;
}

/**
 * Why a paper's item count may not be what the cards say it is.
 *
 * `itemBankProvenanceForBlock` returns null the moment a block's marks, type or difficulty no
 * longer match its provenance — an ordinary thing to do, since the editor lets a teacher retype a
 * block's marks. `blocksForItemCount` then falls back to ONE row for that block. For a 20-item
 * card that silently collapses the paper's weight from 20 items to 1, and every difficulty and
 * thinking-level percentage downstream is computed on the wrong denominator with nothing said.
 */
export function itemCountNotes(blocks: EditorBlock[]): ItemCountNote[] {
  const notes: ItemCountNote[] = [];
  for (const block of blocks) {
    const stated = itemBankProvenance(block.content);
    if (!stated || stated.items <= 1) continue;
    const verified = itemBankProvenanceForBlock(block);
    if (!verified) {
      notes.push({
        code: "item-count-collapsed",
        blockId: block.id,
        cardId: stated.card_id,
        message:
          `Card ${stated.card_id} was imported as ${stated.items} items, but this block no longer ` +
          `matches the card it came from, so it counts as 1 item here. Re-import the card, or set ` +
          `its marks back to ${stated.marks}, before trusting the splits below.`,
      });
      continue;
    }
    if (!block.bloom && verified.kca && !kcaSpread(verified.kca, verified.items)) {
      notes.push({
        code: "kca-unusable",
        blockId: block.id,
        cardId: verified.card_id,
        message:
          `Card ${verified.card_id} states a thinking level for ${verified.kca.k + verified.kca.c + verified.kca.a} ` +
          `items but carries ${verified.items}. Its level is not used; tag this block yourself.`,
      });
    }
  }
  return notes;
}
